import { useCallback, useEffect, useId, useRef, useState, } from "react";
let activeId = null;
const listeners = new Set();
const claim = (id) => {
    if (activeId === id)
        return;
    activeId = id;
    for (const listener of listeners)
        listener(id);
};
let lastInputWasKeyboard = true;
let inputTrackers = 0;
const markKeyboard = () => {
    lastInputWasKeyboard = true;
};
const markPointer = () => {
    lastInputWasKeyboard = false;
};
const isTouchPrimary = () => {
    try {
        return window.matchMedia("(hover: none)").matches;
    }
    catch {
        return false;
    }
};
const clearTimer = (timer) => {
    if (timer.current)
        clearTimeout(timer.current);
    timer.current = null;
};
/**
 * Hover and focus controller for {@link Tooltip}. Opens after `delay` while the
 * pointer rests on the trigger or keyboard focus sits in it. After the pointer leaves
 * the trigger or the bubble it stays for `closeDelay`; blur, a press on the trigger,
 * or another tooltip opening close it at once. On touch-primary devices nothing opens
 * unless `touch` is `toggle`.
 *
 * @example
 * const tip = useTooltip<HTMLButtonElement>({ placement: "bottom" });
 * <button {...tip.triggerProps}>Save</button>
 * {tip.open && <Tooltip title="Save the curve" anchor={tip.triggerProps.ref.current} placement={tip.placement} />}
 */
export function useTooltip({ placement = "top", origin = "edge", interactive = false, delay = 500, closeDelay = 300, disabled = false, touch = "none", } = {}) {
    const id = useId();
    const ref = useRef(null);
    const openTimer = useRef(null);
    const closeTimer = useRef(null);
    const [open, setOpen] = useState(false);
    const closeNow = useCallback(() => {
        clearTimer(openTimer);
        clearTimer(closeTimer);
        setOpen(false);
        if (activeId === id)
            claim(null);
    }, [id]);
    const scheduleClose = () => {
        clearTimer(openTimer);
        clearTimer(closeTimer);
        closeTimer.current = setTimeout(closeNow, closeDelay);
    };
    const scheduleOpen = () => {
        if (disabled || isTouchPrimary())
            return;
        clearTimer(closeTimer);
        if (open || openTimer.current)
            return;
        claim(id);
        openTimer.current = setTimeout(() => {
            openTimer.current = null;
            setOpen(true);
        }, delay);
    };
    const tapToggle = touch === "toggle" && isTouchPrimary();
    useEffect(() => {
        if (inputTrackers++ === 0) {
            document.addEventListener("keydown", markKeyboard, true);
            document.addEventListener("mousedown", markPointer, true);
            document.addEventListener("touchstart", markPointer, true);
        }
        const onClaim = (claimedId) => {
            if (claimedId !== id)
                closeNow();
        };
        listeners.add(onClaim);
        return () => {
            listeners.delete(onClaim);
            closeNow();
            if (--inputTrackers === 0) {
                document.removeEventListener("keydown", markKeyboard, true);
                document.removeEventListener("mousedown", markPointer, true);
                document.removeEventListener("touchstart", markPointer, true);
            }
        };
    }, [id, closeNow]);
    useEffect(() => {
        if (disabled)
            closeNow();
    }, [disabled, closeNow]);
    useEffect(() => {
        if (!open || !tapToggle)
            return;
        const dismiss = (event) => {
            if (!ref.current?.contains(event.target))
                closeNow();
        };
        document.addEventListener("mousedown", dismiss);
        document.addEventListener("touchstart", dismiss, { passive: true });
        return () => {
            document.removeEventListener("mousedown", dismiss);
            document.removeEventListener("touchstart", dismiss);
        };
    }, [open, tapToggle, closeNow]);
    return {
        open,
        placement,
        origin,
        interactive,
        triggerProps: {
            ref,
            onMouseEnter: scheduleOpen,
            onMouseLeave: scheduleClose,
            onFocus: () => {
                if (lastInputWasKeyboard)
                    scheduleOpen();
            },
            onBlur: closeNow,
            onMouseDown: (event) => {
                if (tapToggle || !ref.current?.contains(event.target))
                    return;
                closeNow();
            },
            onClick: (event) => {
                if (!tapToggle || disabled)
                    return;
                event.stopPropagation();
                if (open) {
                    closeNow();
                    return;
                }
                claim(id);
                setOpen(true);
            },
            onKeyDown: (event) => {
                if (event.key === "Escape" && open)
                    closeNow();
            },
        },
        bubbleProps: {
            onMouseEnter: () => clearTimer(closeTimer),
            onMouseLeave: scheduleClose,
        },
    };
}
//# sourceMappingURL=useTooltip.js.map